'use client';

import { c as _c } from "react/compiler-runtime";
import { isLiveActivityPath, LIVE_ACTIVITY_PAGE_TABS, LIVE_ACTIVITY_TAB_VALUES, type LiveActivityPageTabs } from '@/app/(authenticated)/(dashboard)/live-activity/constants';
import { AppShellRailDocsHelpButton, AppShellRailGlobalFiltersItem, AppShellRailProjectPicker, AppShellRailSettingsButton, AppShellRailUserAvatar } from '@/components/layout/dashboard/app-shell-left-rail-items';
import type { AppShellLeftRailConfig, AppShellRailActionItem } from '@/components/layout/dashboard/app-shell-left-rail.types';
import { AppShellRailTooltipContent } from '@/components/layout/dashboard/app-shell-rail-tooltip-content';
import { DEFAULT_ROI_SETTINGS_TAB } from '@/domains/reports/report-page/roi/roi-settings-tabs';
import { PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID, PARAMS_KEY_REPORT_ID, PARAMS_KEY_REPORT_SIDEBAR_SECTION, PARAMS_KEY_SUBPRODUCT } from '@/constants/route-params-keys';
import { useAppNavigation } from '@/domains/navigation/use-app-navigation';
import { useReportSubproductParam } from '@/domains/reports/hooks/use-report-subproduct';
import { shouldShowBetaSubproductUpsell } from '@/domains/reports/utils/report-subproduct-availability.utils';
import type { ReportSubproduct } from '@/domains/reports/utils/report-subproduct.utils';
import { useProjectsContext } from '@/providers/projects-provider';
import { useConfig } from '@/hooks/use-config';
import { useLocalStorageState } from '@/hooks/useSessionState';
import { Calculator, Database, DollarSign, GraduationCap, ListTree, Smile } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { getEmployeeSubproductRailKeys, type EmployeeSubproductRailKey } from '@/components/layout/dashboard/employee-subproduct-rail-order';
import { getSubproductRailHighlightSubproduct, isSubproductRailItemActive } from '@/components/layout/dashboard/subproduct-rail-active';
const LAST_LIVE_ACTIVITY_TAB_STORAGE_KEY = 'nebuly_last_live_activity_tab';
const SIDEBAR_URL_KEEP = [PARAMS_KEY_REPORT_ID, PARAMS_KEY_REPORT_SIDEBAR_SECTION];
export function useAppShellLeftRailConfig() {
  const $ = _c(48);
  const {
    selectedProject,
    projectId
  } = useProjectsContext();
  const {
    navigateApp
  } = useAppNavigation();
  const pathname = usePathname();
  const routeSubproduct = useReportSubproductParam();
  const isReportSubproductRailContext = pathname === "/reports" || pathname === "/explore" || pathname === "/all-reports";
  const originalProjectId = selectedProject?.mappedProjectId ?? projectId;
  const {
    data: projectConfig
  } = useConfig(originalProjectId);
  let t0;
  if ($[0] !== routeSubproduct) {
    t0 = getSubproductRailHighlightSubproduct(routeSubproduct);
    $[0] = routeSubproduct;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const railHighlightSubproduct = t0;
  let t1;
  if ($[2] !== projectConfig || $[3] !== routeSubproduct) {
    t1 = shouldShowBetaSubproductUpsell(routeSubproduct, projectConfig);
    $[2] = projectConfig;
    $[3] = routeSubproduct;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const showBetaSubproductUpsell = t1;
  const isRoiEnabled = projectConfig?.roi_settings?.enabled ?? false;
  const isProficiencyEnabled = projectConfig?.aifi_settings?.enabled ?? false;
  const [lastLiveActivityTab, setLastLiveActivityTab] = useLocalStorageState(LAST_LIVE_ACTIVITY_TAB_STORAGE_KEY, LIVE_ACTIVITY_PAGE_TABS.interactions);
  let t2;
  if ($[5] !== lastLiveActivityTab) {
    t2 = LIVE_ACTIVITY_TAB_VALUES.includes(lastLiveActivityTab) ? lastLiveActivityTab : LIVE_ACTIVITY_PAGE_TABS.interactions;
    $[5] = lastLiveActivityTab;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const resolvedLastLiveActivityTab = t2;
  let t3;
  if ($[7] !== pathname) {
    t3 = isLiveActivityPath(pathname);
    $[7] = pathname;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isLiveDataActive = t3;
  const isTaxonomyActive = pathname === "/taxonomy";
  let t4;
  if ($[9] !== pathname) {
    t4 = pathname.startsWith("/roi-setup");
    $[9] = pathname;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const isRoiSetupActive = t4;
  let t5;
  if ($[11] !== navigateApp) {
    t5 = subproduct => {
      navigateApp({
        route: "/reports",
        params: {
          [PARAMS_KEY_SUBPRODUCT]: subproduct,
          [PARAMS_KEY_REPORT_ID]: undefined,
          [PARAMS_KEY_REPORT_SIDEBAR_SECTION]: undefined
        }
      });
    };
    $[11] = navigateApp;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const navigateToSubproduct = t5;
  selectedProject?.product;
  let t6;
  if ($[13] !== isProficiencyEnabled || $[14] !== isReportSubproductRailContext || $[15] !== isRoiEnabled || $[16] !== navigateToSubproduct || $[17] !== pathname || $[18] !== railHighlightSubproduct || $[19] !== routeSubproduct || $[20] !== selectedProject?.product || $[21] !== showBetaSubproductUpsell) {
    bb0: {
      const isEmployeeProduct = selectedProject?.product === "internal";
      let t7;
      if ($[23] !== navigateToSubproduct) {
        t7 = () => navigateToSubproduct("experience");
        $[23] = navigateToSubproduct;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      let t8;
      if ($[25] !== isEmployeeProduct) {
        t8 = () => <AppShellRailTooltipContent title="Experience" description={isEmployeeProduct ? "Understand how employees experience AI, including sentiment, friction, trust, and failure reasons." : "Understand how customers experience AI, including sentiment, friction, trust, and failure reasons."} icon={Smile} subproduct="experience" />;
        $[25] = isEmployeeProduct;
        $[26] = t8;
      } else {
        t8 = $[26];
      }
      let t9;
      if ($[27] !== navigateToSubproduct) {
        t9 = () => navigateToSubproduct("aifi");
        $[27] = navigateToSubproduct;
        $[28] = t9;
      } else {
        t9 = $[28];
      }
      let t10;
      if ($[29] !== navigateToSubproduct) {
        t10 = () => navigateToSubproduct("roi");
        $[29] = navigateToSubproduct;
        $[30] = t10;
      } else {
        t10 = $[30];
      }
      const subproductItems = {
        experience: {
          icon: Smile,
          label: "Experience",
          isActive: isReportSubproductRailContext && railHighlightSubproduct === "experience",
          onClick: t7,
          dataTour: "sidebar-experience-nav",
          renderTooltip: t8
        },
        aifi: {
          icon: GraduationCap,
          label: "Proficiency",
          isActive: isSubproductRailItemActive(pathname, railHighlightSubproduct, "aifi"),
          isDisabled: !isProficiencyEnabled,
          allowActiveWhenDisabled: showBetaSubproductUpsell && routeSubproduct === "aifi",
          onClick: t9,
          dataTour: "sidebar-fluency-nav",
          renderTooltip: _temp
        },
        roi: {
          icon: DollarSign,
          label: "ROI",
          isActive: isSubproductRailItemActive(pathname, railHighlightSubproduct, "roi"),
          isDisabled: !isRoiEnabled,
          allowActiveWhenDisabled: showBetaSubproductUpsell && routeSubproduct === "roi",
          onClick: t10,
          dataTour: "sidebar-roi-nav",
          renderTooltip: _temp2
        }
      };
      if (!isEmployeeProduct) {
        t6 = [subproductItems.experience];
        break bb0;
      }
      t6 = getEmployeeSubproductRailKeys(isProficiencyEnabled, isRoiEnabled).map(key => subproductItems[key]);
    }
    $[13] = isProficiencyEnabled;
    $[14] = isReportSubproductRailContext;
    $[15] = isRoiEnabled;
    $[16] = navigateToSubproduct;
    $[17] = pathname;
    $[18] = railHighlightSubproduct;
    $[19] = routeSubproduct;
    $[20] = selectedProject?.product;
    $[21] = showBetaSubproductUpsell;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const centerTop = t6;
  let t7;
  if ($[31] !== isLiveDataActive || $[32] !== isRoiEnabled || $[33] !== isRoiSetupActive || $[34] !== isTaxonomyActive || $[35] !== navigateApp || $[36] !== resolvedLastLiveActivityTab || $[37] !== selectedProject?.id || $[38] !== selectedProject?.product || $[39] !== setLastLiveActivityTab) {
    t7 = () => {
      const items = [{
        icon: Database,
        label: "Live data",
        isActive: isLiveDataActive,
        dataTour: "sidebar-live-data-nav",
        onClick: () => {
          setLastLiveActivityTab(resolvedLastLiveActivityTab);
          navigateApp({
            route: "live-activity-section",
            section: resolvedLastLiveActivityTab,
            keepList: [...SIDEBAR_URL_KEEP]
          });
        },
        renderTooltip: _temp3
      }, {
        icon: ListTree,
        label: "Taxonomy",
        isActive: isTaxonomyActive,
        dataTour: "sidebar-taxonomy-nav",
        onClick: () => {
          navigateApp({
            route: "/taxonomy",
            keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID, PARAMS_KEY_SUBPRODUCT, ...SIDEBAR_URL_KEEP]
          });
        },
        renderTooltip: _temp4
      }];
      if (selectedProject?.product === "internal" && isRoiEnabled) {
        items.push({
          icon: Calculator,
          label: "ROI Setup",
          isActive: isRoiSetupActive,
          dataTour: "sidebar-roi-setup-nav",
          onClick: () => {
            navigateApp({
              route: "roi-setup-section",
              section: DEFAULT_ROI_SETTINGS_TAB,
              keepList: [PARAMS_KEY_ORGANIZATION_ID, PARAMS_KEY_PROJECT_ID]
            });
          },
          renderTooltip: _temp5
        });
      }
      if (selectedProject?.id) {
        items.push({
          render: _temp6
        });
      }
      return items;
    };
    $[31] = isLiveDataActive;
    $[32] = isRoiEnabled;
    $[33] = isRoiSetupActive;
    $[34] = isTaxonomyActive;
    $[35] = navigateApp;
    $[36] = resolvedLastLiveActivityTab;
    $[37] = selectedProject?.id;
    $[38] = selectedProject?.product;
    $[39] = setLastLiveActivityTab;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  selectedProject?.id;
  selectedProject?.product;
  let t8;
  if ($[41] !== t7) {
    t8 = t7();
    $[41] = t7;
    $[42] = t8;
  } else {
    t8 = $[42];
  }
  const centerBottom = t8;
  let t9;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [{
      render: _temp7
    }];
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [{
      render: _temp8
    }, {
      render: _temp9
    }, {
      render: _temp10
    }];
    $[44] = t10;
  } else {
    t10 = $[44];
  }
  let t11;
  if ($[45] !== centerBottom || $[46] !== centerTop) {
    t11 = {
      top: t9,
      centerTop,
      centerBottom,
      bottom: t10
    };
    $[45] = centerBottom;
    $[46] = centerTop;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  return t11;
}
function _temp10() {
  return <AppShellRailUserAvatar />;
}
function _temp9() {
  return <AppShellRailSettingsButton />;
}
function _temp8() {
  return <AppShellRailDocsHelpButton />;
}
function _temp7() {
  return <AppShellRailProjectPicker />;
}
function _temp6() {
  return <AppShellRailGlobalFiltersItem />;
}
function _temp5() {
  return <AppShellRailTooltipContent title="ROI Setup" icon={Calculator} description="Configure how agent value is calculated, including time saved by task type, hourly costs, and success criteria." />;
}
function _temp4() {
  return <AppShellRailTooltipContent title="Taxonomy" icon={ListTree} description="Customize how Nebuly classifies your interactions, including tasks, complexity, autonomy, and prompt quality." />;
}
function _temp3() {
  return <AppShellRailTooltipContent title="Live Data" icon={Database} description="Explore the raw interactions, conversations, users, and groups behind your reports." />;
}
function _temp2() {
  return <AppShellRailTooltipContent title="ROI" description="Measure the value of your AI agents through time saved, monetary impact, and wasted effort." icon={DollarSign} subproduct="roi" />;
}
function _temp() {
  return <AppShellRailTooltipContent title="Proficiency" description="Understand how effectively users use AI, including proficiency, prompt quality, and upskilling opportunities." icon={GraduationCap} subproduct="aifi" />;
}